'use client';

import { useState, useMemo } from 'react';
import Icon from '@/components/ui/AppIcon';

type NotificationType = 'booking' | 'lead' | 'support' | 'price_alert' | 'system';
type FilterType = 'all' | NotificationType | 'unread';

interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  timestamp: Date;
  isRead: boolean;
  priority: 'low' | 'medium' | 'high' | 'critical';
  actionUrl?: string;
  metadata?: Record<string, string>;
}

const TYPE_CONFIG: Record<NotificationType, { label: string; icon: string; color: string; bg: string }> = {
  booking: { label: 'Booking', icon: 'TicketIcon', color: 'text-blue-600', bg: 'bg-blue-100' },
  lead: { label: 'Lead', icon: 'UserPlusIcon', color: 'text-purple-600', bg: 'bg-purple-100' },
  support: { label: 'Support', icon: 'ChatBubbleLeftRightIcon', color: 'text-orange-600', bg: 'bg-orange-100' },
  price_alert: { label: 'Price Alert', icon: 'CurrencyPoundIcon', color: 'text-green-600', bg: 'bg-green-100' },
  system: { label: 'System', icon: 'CpuChipIcon', color: 'text-gray-600', bg: 'bg-gray-100' },
};

const PRIORITY_CONFIG = {
  low: { label: 'Low', color: 'text-gray-500', dot: 'bg-gray-400' },
  medium: { label: 'Medium', color: 'text-yellow-600', dot: 'bg-yellow-400' },
  high: { label: 'High', color: 'text-orange-600', dot: 'bg-orange-500' },
  critical: { label: 'Critical', color: 'text-red-600', dot: 'bg-red-500' },
};

const INITIAL_NOTIFICATIONS: Notification[] = [
  { id: '1', type: 'booking', title: 'New Booking Confirmed', message: 'Booking AB3K7X confirmed for London → Dubai on 15 Mar 2026. Total: £842.', timestamp: new Date(Date.now() - 5 * 60000), isRead: false, priority: 'high', metadata: { pnr: 'AB3K7X', route: 'LHR→DXB' } },
  { id: '2', type: 'lead', title: 'New Lead Captured', message: 'Sarah Johnson submitted a booking interest form for Maldives holiday package (Budget: £3,500).', timestamp: new Date(Date.now() - 12 * 60000), isRead: false, priority: 'high', metadata: { name: 'Sarah Johnson', budget: '£3,500' } },
  { id: '3', type: 'support', title: 'Support Ticket Opened', message: 'Ticket #TKT-2847 opened by James Wilson regarding flight cancellation refund request.', timestamp: new Date(Date.now() - 25 * 60000), isRead: false, priority: 'medium', metadata: { ticket: '#TKT-2847' } },
  { id: '4', type: 'price_alert', title: 'Price Drop Alert', message: 'London → New York price dropped by £127 (from £689 to £562). 3 users tracking this route.', timestamp: new Date(Date.now() - 45 * 60000), isRead: true, priority: 'medium', metadata: { route: 'LHR→JFK', saving: '£127' } },
  { id: '5', type: 'system', title: 'API Rate Limit Warning', message: 'Anthropic API usage at 85% of monthly quota. Consider upgrading plan to avoid disruptions.', timestamp: new Date(Date.now() - 1 * 3600000), isRead: false, priority: 'critical', metadata: { usage: '85%' } },
  { id: '6', type: 'booking', title: 'Booking Modification Request', message: 'Customer Michael Brown requesting date change for booking CD9P2Q (Manchester → Barcelona).', timestamp: new Date(Date.now() - 2 * 3600000), isRead: true, priority: 'medium', metadata: { pnr: 'CD9P2Q' } },
  { id: '7', type: 'lead', title: 'Hot Lead - High Value', message: 'Corporate inquiry from TechCorp Ltd for 12 business class seats to Singapore. Estimated value: £28,400.', timestamp: new Date(Date.now() - 3 * 3600000), isRead: false, priority: 'critical', metadata: { company: 'TechCorp Ltd', value: '£28,400' } },
  { id: '8', type: 'support', title: 'Ticket Escalated', message: 'Ticket #TKT-2831 escalated to senior support. Customer waiting 48+ hours for visa documentation.', timestamp: new Date(Date.now() - 4 * 3600000), isRead: true, priority: 'high', metadata: { ticket: '#TKT-2831' } },
  { id: '9', type: 'price_alert', title: 'Price Surge Alert', message: 'London → Bali prices increased 34% due to peak season. Alert sent to 7 monitoring users.', timestamp: new Date(Date.now() - 5 * 3600000), isRead: true, priority: 'low', metadata: { route: 'LHR→DPS', change: '+34%' } },
  { id: '10', type: 'system', title: 'Scheduled Maintenance', message: 'System maintenance scheduled for 02:00–04:00 GMT on 5 Mar 2026. Brief downtime expected.', timestamp: new Date(Date.now() - 6 * 3600000), isRead: true, priority: 'medium', metadata: { time: '02:00–04:00 GMT' } },
  { id: '11', type: 'booking', title: 'Payment Failed', message: 'Payment failed for booking attempt by Emma Davis (LHR→CDG). Card declined. Follow-up required.', timestamp: new Date(Date.now() - 8 * 3600000), isRead: false, priority: 'high', metadata: { customer: 'Emma Davis' } },
  { id: '12', type: 'lead', title: 'Lead Converted', message: 'Lead from Robert Chen successfully converted to booking EF5R8T. Commission: £124.', timestamp: new Date(Date.now() - 10 * 3600000), isRead: true, priority: 'low', metadata: { pnr: 'EF5R8T', commission: '£124' } },
  { id: '13', type: 'system', title: 'New Partner Integration', message: 'Expedia affiliate integration activated. New revenue stream enabled for hotel bookings.', timestamp: new Date(Date.now() - 24 * 3600000), isRead: true, priority: 'low', metadata: { partner: 'Expedia' } },
  { id: '14', type: 'support', title: 'Positive Review Received', message: 'Customer left 5-star review: "Excellent service, found cheapest flights instantly!" — Priya Sharma', timestamp: new Date(Date.now() - 26 * 3600000), isRead: true, priority: 'low', metadata: { rating: '5 stars' } },
  { id: '15', type: 'price_alert', title: 'Best Price Found', message: 'AI found best price for tracked route LHR→BKK: £487 via Thai Airways. Alert sent to 5 users.', timestamp: new Date(Date.now() - 30 * 3600000), isRead: true, priority: 'medium', metadata: { route: 'LHR→BKK', price: '£487' } },
];

function timeAgo(date: Date): string {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export default function NotificationCenterInteractive() {
  const [notifications, setNotifications] = useState<Notification[]>(INITIAL_NOTIFICATIONS);
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'newest' | 'oldest' | 'priority'>('newest');

  const filters: { id: FilterType; label: string; icon: string }[] = [
    { id: 'all', label: 'All', icon: 'Squares2X2Icon' },
    { id: 'unread', label: 'Unread', icon: 'EnvelopeIcon' },
    { id: 'booking', label: 'Bookings', icon: 'TicketIcon' },
    { id: 'lead', label: 'Leads', icon: 'UserPlusIcon' },
    { id: 'support', label: 'Support', icon: 'ChatBubbleLeftRightIcon' },
    { id: 'price_alert', label: 'Price Alerts', icon: 'CurrencyPoundIcon' },
    { id: 'system', label: 'System', icon: 'CpuChipIcon' },
  ];

  const filteredNotifications = useMemo(() => {
    let result = [...notifications];
    if (activeFilter === 'unread') result = result.filter(n => !n.isRead);
    else if (activeFilter !== 'all') result = result.filter(n => n.type === activeFilter);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(n => n.title.toLowerCase().includes(q) || n.message.toLowerCase().includes(q));
    }
    if (sortBy === 'newest') result.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
    else if (sortBy === 'oldest') result.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
    else if (sortBy === 'priority') {
      const order = { critical: 0, high: 1, medium: 2, low: 3 };
      result.sort((a, b) => order[a.priority] - order[b.priority]);
    }
    return result;
  }, [notifications, activeFilter, searchQuery, sortBy]);

  const unreadCount = notifications.filter(n => !n.isRead).length;
  const stats = {
    total: notifications.length,
    unread: unreadCount,
    critical: notifications.filter(n => n.priority === 'critical' && !n.isRead).length,
    today: notifications.filter(n => Date.now() - n.timestamp.getTime() < 24 * 3600000).length,
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredNotifications.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(filteredNotifications.map(n => n.id)));
  };

  const markSelectedRead = () => {
    setNotifications(prev => prev.map(n => selectedIds.has(n.id) ? { ...n, isRead: true } : n));
    setSelectedIds(new Set());
  };

  const markSelectedUnread = () => {
    setNotifications(prev => prev.map(n => selectedIds.has(n.id) ? { ...n, isRead: false } : n));
    setSelectedIds(new Set());
  };

  const deleteSelected = () => {
    setNotifications(prev => prev.filter(n => !selectedIds.has(n.id)));
    setSelectedIds(new Set());
  };

  const markAllRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
  };

  const markOneRead = (id: string) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, isRead: true } : n));
  };

  return (
    <div className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center shadow-lg">
              <Icon name="BellIcon" size={24} className="text-white" />
            </div>
            <div>
              <h1 className="font-heading text-3xl md:text-4xl font-black text-foreground">Notification Center</h1>
              <p className="text-muted-foreground text-sm">All system notifications in one place</p>
            </div>
          </div>
          <button
            onClick={markAllRead}
            className="hidden sm:flex items-center gap-2 px-4 py-2 text-sm font-bold text-primary border border-primary/30 rounded-lg hover:bg-primary/5 transition-colors"
          >
            <Icon name="CheckCircleIcon" size={16} />
            Mark All Read
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Total', value: stats.total, icon: 'BellIcon', color: 'text-primary', bg: 'bg-primary/10' },
          { label: 'Unread', value: stats.unread, icon: 'EnvelopeIcon', color: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'Critical', value: stats.critical, icon: 'ExclamationCircleIcon', color: 'text-red-600', bg: 'bg-red-50' },
          { label: 'Today', value: stats.today, icon: 'CalendarDaysIcon', color: 'text-green-600', bg: 'bg-green-50' },
        ].map(stat => (
          <div key={stat.label} className="bg-white rounded-xl border border-border p-4 flex items-center gap-3 shadow-sm">
            <div className={`w-10 h-10 ${stat.bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
              <Icon name={stat.icon as any} size={20} className={stat.color} />
            </div>
            <div>
              <p className={`text-2xl font-black ${stat.color}`}>{stat.value}</p>
              <p className="text-xs text-muted-foreground font-medium">{stat.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters + Search */}
      <div className="bg-white rounded-xl border border-border shadow-sm mb-4">
        <div className="p-4 border-b border-border">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Icon name="MagnifyingGlassIcon" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search notifications..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              />
            </div>
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value as any)}
              className="px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 bg-white"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="priority">By Priority</option>
            </select>
          </div>
        </div>
        <div className="flex overflow-x-auto p-3 gap-2">
          {filters.map(f => {
            const count = f.id === 'all' ? notifications.length
              : f.id === 'unread' ? notifications.filter(n => !n.isRead).length
              : notifications.filter(n => n.type === f.id).length;
            return (
              <button
                key={f.id}
                onClick={() => setActiveFilter(f.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-bold whitespace-nowrap transition-all ${
                  activeFilter === f.id
                    ? 'bg-primary text-white shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-surface'
                }`}
              >
                <Icon name={f.icon as any} size={14} />
                {f.label}
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-black ${
                  activeFilter === f.id ? 'bg-white/20 text-white' : 'bg-surface text-muted-foreground'
                }`}>{count}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selectedIds.size > 0 && (
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 mb-4 flex items-center justify-between">
          <span className="text-sm font-bold text-primary">{selectedIds.size} selected</span>
          <div className="flex items-center gap-2">
            <button onClick={markSelectedRead} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors">
              <Icon name="CheckIcon" size={14} /> Mark Read
            </button>
            <button onClick={markSelectedUnread} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors">
              <Icon name="EnvelopeIcon" size={14} /> Mark Unread
            </button>
            <button onClick={deleteSelected} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors">
              <Icon name="TrashIcon" size={14} /> Delete
            </button>
          </div>
        </div>
      )}

      {/* Notification List */}
      <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden">
        {/* List Header */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border bg-surface/50">
          <input
            type="checkbox"
            checked={selectedIds.size === filteredNotifications.length && filteredNotifications.length > 0}
            onChange={toggleSelectAll}
            className="w-4 h-4 rounded border-border text-primary focus:ring-primary/30"
          />
          <span className="text-sm text-muted-foreground font-medium">
            {filteredNotifications.length} notification{filteredNotifications.length !== 1 ? 's' : ''}
          </span>
        </div>

        {filteredNotifications.length === 0 ? (
          <div className="py-16 text-center">
            <Icon name="BellSlashIcon" size={48} className="text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-muted-foreground font-medium">No notifications found</p>
            <p className="text-sm text-muted-foreground/70">Try adjusting your filters</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {filteredNotifications.map(notification => {
              const typeConf = TYPE_CONFIG[notification.type];
              const priorityConf = PRIORITY_CONFIG[notification.priority];
              return (
                <div
                  key={notification.id}
                  className={`flex items-start gap-3 px-4 py-4 hover:bg-surface/50 transition-colors cursor-pointer ${
                    !notification.isRead ? 'bg-primary/2' : ''
                  }`}
                  onClick={() => markOneRead(notification.id)}
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.has(notification.id)}
                    onChange={e => { e.stopPropagation(); toggleSelect(notification.id); }}
                    onClick={e => e.stopPropagation()}
                    className="w-4 h-4 rounded border-border text-primary focus:ring-primary/30 mt-1 flex-shrink-0"
                  />
                  {/* Unread dot */}
                  <div className="flex-shrink-0 mt-1.5">
                    {!notification.isRead
                      ? <div className="w-2 h-2 bg-primary rounded-full"></div>
                      : <div className="w-2 h-2"></div>
                    }
                  </div>
                  {/* Type Icon */}
                  <div className={`w-9 h-9 ${typeConf.bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                    <Icon name={typeConf.icon as any} size={18} className={typeConf.color} />
                  </div>
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className={`text-sm font-bold ${!notification.isRead ? 'text-foreground' : 'text-muted-foreground'}`}>
                        {notification.title}
                      </p>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <div className="flex items-center gap-1">
                          <div className={`w-1.5 h-1.5 rounded-full ${priorityConf.dot}`}></div>
                          <span className={`text-xs font-bold ${priorityConf.color}`}>{priorityConf.label}</span>
                        </div>
                        <span className="text-xs text-muted-foreground">{timeAgo(notification.timestamp)}</span>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{notification.message}</p>
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${typeConf.bg} ${typeConf.color}`}>
                        {typeConf.label}
                      </span>
                      {notification.metadata && Object.entries(notification.metadata).slice(0, 2).map(([k, v]) => (
                        <span key={k} className="text-xs text-muted-foreground bg-surface px-2 py-0.5 rounded-full">{v}</span>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
